import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Loader2, History, Mail, MessageSquare, Radio, Send, X, RefreshCw, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const CH = {
  email: { label: "Email", icon: Mail, cls: "bg-gold/15 text-gold-deep" },
  sms: { label: "Text", icon: MessageSquare, cls: "bg-sage/20 text-ink" },
  telegram: { label: "Telegram", icon: Radio, cls: "bg-ink/10 text-ink-soft" },
};

export default function AdminSendLog() {
  const [token] = useState(() => getAdminToken());
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [data, setData] = useState({ entries: [], totals: {}, total_delivered: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);       // { id, title } selected row
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/send-log`, auth);
      setData(data);
    } catch (e) {
      if (e.response?.status === 401) adminLogout();
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (row) => {
    if (row.channel !== "email") return;
    setDetail(row);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/campaigns/${row.id}/sends`, auth);
      setDetailData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load this campaign's delivery details.");
      setDetail(null);
    } finally { setDetailLoading(false); }
  }, [auth]);

  const retryFailed = useCallback(async () => {
    if (!detail) return;
    setRetrying(true);
    try {
      const { data } = await axios.post(`${API}/admin/campaigns/${detail.id}/retry-failed`, {}, auth);
      toast.success(`Resending to ${data.retrying} recipient${data.retrying === 1 ? "" : "s"} who didn't get it.`);
      setDetail(null);
      setTimeout(load, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the retry.");
    } finally { setRetrying(false); }
  }, [detail, auth, load]);

  const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
  const entries = (data.entries || []).filter((e) => filter === "all" || e.channel === filter);

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-send-log">
      <div className="mx-auto max-w-5xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <div>
          <h1 className="inline-flex items-center gap-2 font-display text-3xl text-ink">
            <History className="h-6 w-6 text-gold-deep" /> Send Log
          </h1>
          <p className="mt-1 text-sm text-ink-soft">Every email, text and Telegram broadcast you've sent — in one place, with delivery and engagement. Click an email to see failures and resend.</p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="send-log-totals">
          {[["Emails", data.totals?.email || 0], ["Texts", data.totals?.sms || 0], ["Telegram", data.totals?.telegram || 0], ["Total delivered", data.total_delivered || 0]].map(([k, v]) => (
            <div key={k} className="rounded-2xl bg-ivory-50 p-4 text-center ring-1 ring-ivory-400/50">
              <p className="font-display text-2xl text-ink">{v}</p>
              <p className="text-xs text-ink-muted">{k}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 inline-flex rounded-full bg-ivory-50 p-1 ring-1 ring-ivory-400/60">
          {["all", "email", "sms", "telegram"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`send-log-filter-${f}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-display capitalize transition ${filter === f ? "bg-ink text-ivory-50" : "text-ink-soft hover:text-ink"}`}>
              {f === "all" ? "All" : CH[f].label}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 py-6 sm:px-8">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>
        ) : entries.length === 0 ? (
          <div className="rounded-2xl bg-ivory-50 p-10 text-center ring-1 ring-ivory-400/50" data-testid="send-log-empty">
            <Send className="mx-auto h-7 w-7 text-ink-muted" />
            <p className="mt-3 font-display text-lg text-ink">Nothing sent yet</p>
            <p className="mt-1 text-sm text-ink-muted">Your campaigns and broadcasts will appear here once they go out.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl ring-1 ring-ivory-400/50">
            <table className="w-full text-left text-sm">
              <thead className="bg-ivory-50 text-[11px] uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-4 py-3">Channel</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3 text-right">Recipients</th>
                  <th className="px-4 py-3 text-right">Delivered</th>
                  <th className="px-4 py-3 text-right">Opens</th>
                  <th className="px-4 py-3 text-right">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const meta = CH[e.channel] || CH.email;
                  const Icon = meta.icon;
                  const clickable = e.channel === "email";
                  return (
                    <tr key={`${e.channel}-${e.id}`}
                      onClick={() => clickable && openDetail(e)}
                      className={`border-t border-ivory-400/40 bg-white/60 ${clickable ? "cursor-pointer hover:bg-gold/5" : ""}`}
                      data-testid={`send-log-row-${e.id}`}>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] ${meta.cls}`}>
                          <Icon className="h-3.5 w-3.5" /> {meta.label}
                        </span>
                      </td>
                      <td className="max-w-[240px] truncate px-4 py-3 text-ink">
                        {e.title}
                        {e.status === "sending" && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-sage/20 px-2 py-0.5 text-[10px] text-ink">
                            {e.paused ? <><Clock className="h-3 w-3" /> paused · continues {fmt(e.resume_at)}</> : <><Loader2 className="h-3 w-3 animate-spin" /> sending{e.pending ? ` · ${e.pending} left` : ""}</>}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{fmt(e.at)}</td>
                      <td className="px-4 py-3 text-right text-ink">{e.recipients ?? 0}</td>
                      <td className="px-4 py-3 text-right text-ink">{e.delivered ?? 0}{e.failed ? <span className="text-terracotta"> · {e.failed} failed</span> : ""}</td>
                      <td className="px-4 py-3 text-right text-ink">{e.opens == null ? "—" : e.opens}</td>
                      <td className="px-4 py-3 text-right text-ink">{e.clicks == null ? "—" : e.clicks}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-ink-muted">Opens and clicks are tracked for email; texts and Telegram show delivery counts.</p>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-ink/30" onClick={() => setDetail(null)} data-testid="send-log-detail">
          <div className="h-full w-full max-w-md overflow-y-auto bg-ivory-50 shadow-2xl" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start justify-between border-b border-ivory-400/50 p-5">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-ink-muted">Delivery detail</p>
                <h2 className="mt-1 max-w-[18rem] font-display text-lg text-ink">{detail.title}</h2>
              </div>
              <button onClick={() => setDetail(null)} data-testid="send-log-detail-close" className="rounded-full p-1.5 text-ink-muted hover:bg-ivory-200 hover:text-ink"><X className="h-5 w-5" /></button>
            </div>

            {detailLoading || !detailData ? (
              <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>
            ) : (
              <div className="p-5">
                <div className="grid grid-cols-3 gap-3" data-testid="send-log-detail-counts">
                  {[["Delivered", detailData.counts?.sent || 0, "text-gold-deep", CheckCircle2],
                    ["Failed", detailData.counts?.failed || 0, "text-terracotta", AlertTriangle],
                    ["Pending", detailData.counts?.pending || 0, "text-ink-soft", Clock]].map(([k, v, c, I]) => (
                    <div key={k} className="rounded-xl bg-white/70 p-3 text-center ring-1 ring-ivory-400/50">
                      <I className={`mx-auto h-4 w-4 ${c}`} />
                      <p className={`mt-1 font-display text-xl ${c}`}>{v}</p>
                      <p className="text-[11px] text-ink-muted">{k}</p>
                    </div>
                  ))}
                </div>

                {detailData.resume_at && detailData.status === "sending" && (
                  <div className="mt-4 rounded-xl bg-sage/15 p-3 text-xs text-ink">
                    <Clock className="mr-1 inline h-3.5 w-3.5" />
                    Paused at the daily sending limit — it will continue automatically on {fmt(detailData.resume_at)}.
                    {detailData.pause_reason ? <span className="mt-1 block text-ink-muted">{detailData.pause_reason}</span> : null}
                  </div>
                )}

                <div className="mt-5">
                  <h3 className="font-display text-sm text-ink">Why sends failed</h3>
                  {(detailData.failures || []).length === 0 ? (
                    <p className="mt-2 text-sm text-ink-muted">No failures recorded for this campaign.</p>
                  ) : (
                    <ul className="mt-2 space-y-2" data-testid="send-log-detail-failures">
                      {detailData.failures.map((f, i) => (
                        <li key={i} className="rounded-xl bg-white/70 p-3 ring-1 ring-ivory-400/50">
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm text-ink">{f.reason}</p>
                            <span className="shrink-0 rounded-full bg-terracotta/15 px-2 py-0.5 text-[11px] text-terracotta">{f.count}</span>
                          </div>
                          {f.emails?.length ? <p className="mt-1 truncate text-[11px] text-ink-muted">{f.emails.join(", ")}{f.count > f.emails.length ? "…" : ""}</p> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <button
                  onClick={retryFailed}
                  disabled={retrying || detailData.status === "sending" || (detailData.counts?.failed || 0) === 0}
                  data-testid="send-log-retry-failed"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 font-display text-ivory-50 disabled:cursor-not-allowed disabled:opacity-50">
                  {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Retry failed only ({detailData.counts?.failed || 0})
                </button>
                <p className="mt-2 text-center text-[11px] text-ink-muted">Resends only to those who didn't get it — never to the {detailData.counts?.sent || 0} already delivered.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
